import { NavLink, Outlet } from 'react-router-dom'
import { useLiveQuery } from 'dexie-react-hooks'
import { House, ArrowsClockwise, DotsThreeOutline } from '@phosphor-icons/react'
import { useI18n } from '@/i18n'
import { usePhoneSession } from '@/auth/pin/phoneSession'
import { roster, useDeviceInfo, useSyncStatus } from '@/offline'
import { V1Header, V1Sprite } from '@/features/gate/v1/kit'
import { C } from '@/features/gate/v1/style'
import { cn } from '@/lib/cn'
import { OfflineBanner } from './OfflineBanner'

/**
 * Phone shell in the alignment map's look (the V1 boards): the board header's top row — who is
 * working, records still to send, मदद — an offline banner, and the big bottom nav. Language and
 * lock live on More. On a Seva Mandir V1 village phone, Home is the board's आज screen.
 */
export function PhoneLayout() {
  const { t } = useI18n()
  const { actor } = usePhoneSession()
  const { registration } = useDeviceInfo()
  const sync = useSyncStatus()
  const gateConfig = useLiveQuery(() => roster.gateConfig(), [])
  const v1Village = registration?.team.kind === 'VILLAGE' && gateConfig?.v1 === true
  const NAV = [
    { to: v1Village ? '/m/gate' : '/m', end: !v1Village, key: 'nav.home', icon: House },
    { to: '/m/sync', end: false, key: 'nav.sync', icon: ArrowsClockwise },
    { to: '/m/more', end: false, key: 'nav.more', icon: DotsThreeOutline },
  ] as const

  return (
    <div className="mx-auto flex min-h-dvh max-w-screen-sm flex-col bg-paper">
      <header
        data-v1-screen
        className="pt-safe sticky top-0 z-30"
        style={{ background: C.bg, borderBottom: `1px solid ${C.rule}` }}
      >
        <V1Sprite />
        <V1Header
          pending={sync.pending}
          updatable
          farmer={
            actor
              ? {
                  name: actor.name,
                  line: registration?.team.name ?? '',
                  photoLabel: actor.name,
                }
              : undefined
          }
        />
        <OfflineBanner />
      </header>

      <main className="flex-1 px-4 pb-28 pt-4">
        <Outlet />
      </main>

      <nav
        aria-label={t('a11y.mainNav')}
        className="pb-safe fixed inset-x-0 bottom-0 z-30 mx-auto max-w-screen-sm border-t border-rule bg-paper/95 backdrop-blur"
      >
        <ul className="grid grid-cols-3">
          {NAV.map((n) => (
            <li key={n.to}>
              <NavLink
                to={n.to}
                end={n.end}
                className={({ isActive }) =>
                  cn(
                    'flex h-16 flex-col items-center justify-center gap-0.5 text-[12px] font-semibold transition-colors',
                    isActive ? 'text-amber-dark' : 'text-muted hover:text-ink',
                  )
                }
              >
                {({ isActive }) => (
                  <>
                    <n.icon size={24} weight={isActive ? 'fill' : 'regular'} aria-hidden="true" />
                    {t(n.key)}
                  </>
                )}
              </NavLink>
            </li>
          ))}
        </ul>
      </nav>
    </div>
  )
}
